/**
 * moonshot-split-recovery.js
 *
 * Site-wide pending-pickup-order recovery banner. When a customer has
 * a saved pending pickup checkout (left over after their ship order
 * completed, or abandoned mid-flow), this script renders a calm,
 * persistent banner at the top of every storefront page:
 *
 *   "You still have a pickup order waiting — 2 items, $19.98. [Finish checkout →]"
 *
 * The customer can resume from any page; clicking the CTA triggers
 * MoonshotSplit.resumePickup() which restores the items and bounces
 * to /checkout.
 *
 * Behavior:
 *   - Reads pending state via MoonshotSplit.getPending() (localStorage
 *     first; Worker fallback for logged-in cross-device sync).
 *   - Shows on every page EXCEPT /cart and /checkout (those have their
 *     own dedicated UIs).
 *   - Dismissable per page-load via × (does NOT clear the pending
 *     state itself — only clearPending() does that). The banner
 *     reappears on the next navigation as a gentle nudge.
 *   - If the customer wants to actually discard the pending order,
 *     they can use the /cart page's "Discard pickup order" button
 *     (added in cart-page UI).
 */
(function () {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const BANNER_ID = "moonshot-split-recovery-banner";
  const STYLE_ID = "moonshot-split-recovery-styles";
  const BODY_CLASS = "moonshot-split-recovery-active";
  const DISMISS_SESSION_KEY = "moonshot_split_recovery_dismissed_at";
  // Dismiss expires after 12 hours so we don't go silent forever.
  const DISMISS_TTL_MS = 12 * 60 * 60 * 1000;

  function pageIsExcluded() {
    const p = window.location.pathname;
    if (p === "/cart" || p.indexOf("/cart?") === 0) return true;
    if (p.indexOf("/checkouts") === 0) return true;
    if (p.indexOf("/orders") === 0) return true; // order status pages
    return false;
  }

  function dismissedRecently() {
    try {
      const raw = sessionStorage.getItem(DISMISS_SESSION_KEY);
      if (!raw) return false;
      const ts = parseInt(raw, 10);
      if (!ts || isNaN(ts)) return false;
      return Date.now() - ts < DISMISS_TTL_MS;
    } catch (e) {
      return false;
    }
  }

  function markDismissed() {
    try {
      sessionStorage.setItem(DISMISS_SESSION_KEY, String(Date.now()));
    } catch (e) {
      /* ignore */
    }
  }

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      @keyframes moonshot-split-banner-in {
        from { transform: translateY(-100%); opacity: 0; }
        to   { transform: translateY(0);     opacity: 1; }
      }
      #${BANNER_ID} {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 99000;
        background: #1d1f2b;
        color: #ffffff;
        padding: 12px 56px 12px 20px;
        font-size: 0.95rem;
        font-weight: 500;
        line-height: 1.4;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 16px;
        flex-wrap: wrap;
        box-shadow: 0 2px 12px rgba(0,0,0,0.18);
        animation: moonshot-split-banner-in 0.3s ease-out;
      }
      #${BANNER_ID} .moonshot-split-banner__msg {
        flex: 1 1 auto;
        max-width: 600px;
      }
      #${BANNER_ID} .moonshot-split-banner__cta {
        background: #ffffff;
        color: #1d1f2b;
        border: none;
        border-radius: 4px;
        padding: 8px 16px;
        font-size: 0.95rem;
        font-weight: 600;
        cursor: pointer;
        font-family: inherit;
        white-space: nowrap;
      }
      #${BANNER_ID} .moonshot-split-banner__cta:hover {
        background: #f0eee6;
      }
      #${BANNER_ID} .moonshot-split-banner__cta[disabled] {
        opacity: 0.7;
        cursor: wait;
      }
      #${BANNER_ID} .moonshot-split-banner__dismiss {
        position: absolute;
        top: 8px;
        right: 12px;
        background: rgba(255,255,255,0.12);
        border: none;
        border-radius: 4px;
        color: #ffffff;
        cursor: pointer;
        font-size: 1.25rem;
        line-height: 1;
        padding: 4px 10px;
      }
      #${BANNER_ID} .moonshot-split-banner__dismiss:hover {
        background: rgba(255,255,255,0.22);
      }
      body.${BODY_CLASS} {
        padding-top: 56px;
      }
      @media (max-width: 540px) {
        #${BANNER_ID} {
          font-size: 0.88rem;
          padding: 10px 48px 10px 14px;
        }
        body.${BODY_CLASS} {
          padding-top: 76px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function buildBanner(state) {
    const banner = document.createElement("div");
    banner.id = BANNER_ID;
    banner.setAttribute("role", "status");
    banner.setAttribute("aria-live", "polite");

    const count = state.items.reduce(function (acc, it) {
      return acc + (it.quantity || 0);
    }, 0);

    // What modes are in the stash? The stashed items carry their own
    // `fulfillment` property — peek to see what's there. If everything
    // is locker, message accordingly. If everything is pickup, use the
    // existing pickup copy. Mixed (the recursive-split case where ship
    // has just completed and we restored both pickup + locker) → say
    // "more orders waiting".
    let hasStashedPickup = false;
    let hasStashedLocker = false;
    for (const it of state.items) {
      const f = (it.properties && (it.properties.fulfillment || it.properties._fulfillment)) || "ship";
      if (f === "locker") hasStashedLocker = true;
      else if (f.indexOf("pickup:") === 0) hasStashedPickup = true;
    }
    const groupLabel =
      state.pickupGroup === "plainfield" ? "Plainfield" :
      state.pickupGroup === "noblesville" ? "Noblesville" : "your store";

    let messageText;
    if (hasStashedPickup && hasStashedLocker) {
      messageText =
        "You still have more orders waiting — " +
        count +
        " item" +
        (count === 1 ? "" : "s") +
        " across pickup and Locker.";
    } else if (hasStashedLocker) {
      messageText =
        "Your Locker order is ready to finish — " +
        count +
        " item" +
        (count === 1 ? "" : "s") +
        ".";
    } else {
      messageText =
        "You still have a pickup order waiting at " +
        groupLabel +
        " — " +
        count +
        " item" +
        (count === 1 ? "" : "s") +
        ".";
    }

    const msg = document.createElement("span");
    msg.className = "moonshot-split-banner__msg";
    msg.textContent = messageText;
    banner.appendChild(msg);

    const cta = document.createElement("button");
    cta.type = "button";
    cta.className = "moonshot-split-banner__cta";
    cta.textContent = "Finish checkout →";
    cta.addEventListener("click", async function () {
      if (cta.disabled) return;
      cta.disabled = true;
      cta.textContent = "Restoring your items…";
      try {
        await window.MoonshotSplit.resumePickup();
      } catch (err) {
        console.error("[moonshot-split-recovery] resume failed:", err);
        cta.disabled = false;
        cta.textContent = "Finish checkout →";
        alert(
          "Couldn't restore your pickup order. Please open your cart and try again.",
        );
      }
    });
    banner.appendChild(cta);

    const dismiss = document.createElement("button");
    dismiss.type = "button";
    dismiss.className = "moonshot-split-banner__dismiss";
    dismiss.setAttribute("aria-label", "Dismiss");
    dismiss.textContent = "×";
    dismiss.addEventListener("click", function () {
      markDismissed();
      banner.remove();
      document.body.classList.remove(BODY_CLASS);
    });
    banner.appendChild(dismiss);

    return banner;
  }

  async function tryShow() {
    if (pageIsExcluded()) return;
    if (dismissedRecently()) return;
    if (!window.MoonshotSplit || !window.MoonshotSplit.getPending) return;
    let state;
    try {
      state = await window.MoonshotSplit.getPending();
    } catch (e) {
      return;
    }
    if (!state || !Array.isArray(state.items) || state.items.length === 0) return;

    injectStyles();
    const existing = document.getElementById(BANNER_ID);
    if (existing) existing.remove();
    const banner = buildBanner(state);
    document.body.appendChild(banner);
    document.body.classList.add(BODY_CLASS);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tryShow);
  } else {
    tryShow();
  }
})();
